import { useDropzone } from 'react-dropzone';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Gap, List, Panel, theme } from '@salecycle/ui-component-library';
import { useImportConsentContext } from '../context/ImportConsentContext';
import { ConsentFileType, ConsentFileData } from '../types';
import { ConsentFileReader } from '../utils/ConsentFileReader';
import { ConsentListItem } from './ConsentListItem';
import { ConsentDropArea } from './ConsentDropArea';
import { ConsentFileDataParser } from '../utils/ConsentFileDataParser';

type Props = {
  fileType: ConsentFileType;
  disabled?: boolean;
};

export function DropArea({ fileType, disabled }: Props) {
  const { fileData, filesToProcess, setFileData, setFilesToProcess } = useImportConsentContext();
  const { t } = useTranslation('fileManagement');

  const onDrop = useCallback(
    droppedFiles => {
      let filesDropped = droppedFiles.length;
      const newFilesToProcess = {};
      newFilesToProcess[fileType] = filesDropped;
      setFilesToProcess({ ...filesToProcess, ...newFilesToProcess });
      let currentFileData = fileData;
      droppedFiles.forEach(async (file: File) => {
        if (!currentFileData.some(cfd => cfd.fileName === file.name)) {
          const parser = new ConsentFileDataParser(t, file.name, fileType);
          try {
            parser.parse(await new ConsentFileReader().resolve(file, t));
          } catch (e) {
            parser.addError(e as Error);
          }

          currentFileData = currentFileData.concat(parser.data());
          setFileData(currentFileData);
        }

        newFilesToProcess[fileType] = --filesDropped;
        setFilesToProcess({ ...filesToProcess, ...newFilesToProcess });
      });
    },
    [fileData, fileType, filesToProcess, setFileData, setFilesToProcess, t],
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled,
  });

  const removeFile = fileName => {
    setFileData(fileData.filter((f: ConsentFileData) => f.fileName !== fileName));
  };

  return (
    <Panel padding={0} variation="clean">
      <div {...getRootProps()} style={{ backgroundColor: isDragActive ? theme.vars.color.grey1 : 'white' }}>
        <input {...getInputProps()} />
        <ConsentDropArea fileType={fileType} disabled={disabled} filesToProcess={filesToProcess[fileType]} />
      </div>
      {fileData.length > 0 && (
        <div>
          <Gap top={10} />
          <List>
            {fileData
              .filter(cfd => cfd.status.consentType === fileType)
              .map(cfd => (
                <ConsentListItem key={cfd.fileName} removeFile={removeFile} fileData={cfd} />
              ))}
          </List>
        </div>
      )}
    </Panel>
  );
}
